import { db, doc, getDoc } from '../firebase';

// Generic short-TTL cache for one-time Firestore reads on high-traffic public pages.
//
// WHY: HomePage (the busiest route) remounts on EVERY in-app navigation back to '/',
// re-firing its sections/featured/stats/reviews reads each time, and full page reloads
// re-billed them too. Same layering as src/utils/gamesCache.js: module-level memory
// dedupes within a SPA session, localStorage dedupes across reloads, and an in-flight
// promise map collapses concurrent callers into one network read. Values must be
// JSON-serializable — convert Firestore Timestamps (e.g. toMillis()) in the fetcher
// if the caller renders them, because they do not survive the localStorage round-trip.
const LS_PREFIX = 'fl_doccache_v1:';

const memCache = new Map();   // key -> { data, ts }
const inflight = new Map();   // key -> Promise — collapses concurrent callers

function readLS(key, ttlMs) {
    try {
        const raw = localStorage.getItem(LS_PREFIX + key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || (Date.now() - parsed.ts) >= ttlMs) return null;
        return parsed; // envelope { data, ts } — data itself may legitimately be null
    } catch (_) { return null; }
}

function writeLS(key, data) {
    try { localStorage.setItem(LS_PREFIX + key, JSON.stringify({ data, ts: Date.now() })); } catch (_) { }
}

// Cached arbitrary fetch: memory -> localStorage -> fetcherFn (network).
// `key` must be unique per query shape; `fetcherFn` returns the JSON-serializable data.
export async function getCachedQuery(key, ttlMs, fetcherFn) {
    const now = Date.now();
    const mem = memCache.get(key);
    if (mem && (now - mem.ts) < ttlMs) return mem.data;

    const cached = readLS(key, ttlMs);
    if (cached) {
        memCache.set(key, { data: cached.data, ts: cached.ts });
        return cached.data;
    }

    if (inflight.has(key)) return inflight.get(key);
    const p = (async () => {
        try {
            const data = await fetcherFn();
            memCache.set(key, { data, ts: Date.now() });
            writeLS(key, data);
            return data;
        } finally {
            inflight.delete(key);
        }
    })();
    inflight.set(key, p);
    return p;
}

// Seed a MEMORY-only entry with data just read as part of a wider query (e.g. a list page priming
// its rows' detail keys), stamped now like a getCachedQuery fetch, so getCachedQuery(key, ttlMs, …)
// serves it until its ttlMs runs out. Never writes localStorage. `readStartedAt` = when the wider
// read was issued: an entry stored at or after it may come from a newer read, so it is kept.
export function primeCachedQuery(key, data, readStartedAt = Date.now()) {
    const cur = memCache.get(key);
    if (cur && cur.ts >= readStartedAt) return;
    memCache.set(key, { data, ts: Date.now() });
}

// Cached single-doc read by slash path (e.g. 'public/data/stats/homepage').
// Resolves to the doc's data object, or null when the doc does not exist —
// the null is cached too, so a missing doc costs at most one read per TTL.
export async function getCachedDoc(path, ttlMs) {
    return getCachedQuery('doc:' + path, ttlMs, async () => {
        const snap = await getDoc(doc(db, path));
        return snap.exists() ? snap.data() : null;
    });
}

// Drop one cached entry (e.g. after an admin edit) so the next read is fresh.
// For getCachedDoc entries pass 'doc:' + path.
export function clearCachedKey(key) {
    memCache.delete(key);
    try { localStorage.removeItem(LS_PREFIX + key); } catch (_) { }
}
